import React, { useState } from "react";
import { Button, Drawer } from "antd";
import { FaBell } from "react-icons/fa";
import {
  MODAL_TYPES,
  useGlobalModalContext,
} from "../../context/global-modal-context";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { StorageBox } from "../../core/storage";
import AuthService from "../../services/auth";
import {
  MenuOutlined,
  CloseOutlined,
  PhoneOutlined,
  UserOutlined,
  UserSwitchOutlined,
  LogoutOutlined,
  FileDoneOutlined,
  FileTextOutlined,
  SolutionOutlined,
  HomeOutlined,
  CalendarOutlined,
  CommentOutlined,
} from "@ant-design/icons";
import { useAppStore } from "../../store";
import KobikuulBanner from "./kobikuul-banner";
import mcareLogo from "../../assets/images/poweredbymtn.png";
import defaultUserImage from "../../assets/images/user.png";

const navItems = [
  { to: "/main", end: true, label: "Home", icon: <HomeOutlined /> },
  { to: "/main/appointments", label: "Appointments", icon: <CalendarOutlined /> },
  { to: "/main/prescriptions", label: "Prescriptions", icon: <FileDoneOutlined /> },
  { to: "/main/claims", label: "Claims", icon: <FileTextOutlined /> },
  { to: "/main/chat", label: "Kobikuul Chat", icon: <CommentOutlined /> },
  { to: "/main/profile/1", label: "Profile", icon: <UserOutlined /> },
  { to: "/main/profile/2", label: "Mcare", icon: <SolutionOutlined /> },
  { to: "/main/unsubscribe", label: "Unsubscribe", icon: <UserSwitchOutlined /> },
];

export default function Header(props: any) {
  const { user, logOut } = useAppStore();
  const [open, setOpen] = useState(false);
  const { showModal } = useGlobalModalContext();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isOnKobikuulChat = pathname === "/main/chat";
  const openUserModal = () => {
    showModal(MODAL_TYPES.NOTIFICATION_MODAL, {
      title: "Notifications",
      confirmBtn: "Ok",
    });
  };

  async function onLogout() {
    // Previously this only reset in-memory UI state - the actual session
    // token stayed valid in localStorage and on the server indefinitely.
    try {
      await AuthService.signOut();
    } catch {
      // Best-effort: still clear the local session even if the server
      // call fails (e.g. offline) - don't trap the user mid-logout.
    }
    StorageBox.clearStorage();
    logOut();
    navigate("/sign-in");
  }

  const showDrawer = () => {
    setOpen(true);
  };

  const onClose = () => {
    setOpen(false);
  };

  const userImage = user && user.picture ? user.picture : defaultUserImage;

  return (
    <div className="flex min-w-0 max-w-full justify-between items-center overflow-hidden bg-white shadow-sm h-full w-full lg:px-8 px-4 py-3 gap-3 max-[369px]:px-2 max-[369px]:py-2 max-[369px]:gap-2">
      {/* Greeting */}
      <div className="min-w-0 flex-1 lg:flex-none lg:shrink-0">
        <h2 className="lg:text-3xl text-xl font-semibold max-[369px]:text-base truncate">
          Hello,{" "}
          <span className="text-green-600">{user?.fullname}</span>
        </h2>
        <p className="lg:text-base text-sm text-gray-500 max-[369px]:hidden">
          How can we help you stay healthy today?
        </p>
      </div>

      {/* Desktop Kobikuul — hidden on mobile, and hidden ON the chat page
          itself (a "go to chat" banner is redundant there). Spacer keeps the
          header's three-column layout intact. */}
      {isOnKobikuulChat ? (
        <div className="hidden lg:flex flex-1 min-w-0 px-2" />
      ) : (
        <div className="hidden lg:flex flex-1 justify-center min-w-0 px-2">
          <KobikuulBanner compact />
        </div>
      )}

      {/* Desktop Notification */}
      <div
        className={`hidden lg:flex items-center shrink-0 ${
          props.collapsed ? "mr-24" : "mr-52"
        }`}
      >
        <Button
          onClick={openUserModal}
          className="flex items-center gap-2 font-medium"
          type="default"
        >
          <FaBell className="text-lg" />
          Notification
        </Button>
      </div>

      {/* Mobile Menu + Notification */}
      <div className="lg:hidden flex shrink-0 items-center gap-2 max-[369px]:gap-1">
        <Button
          type="link"
          onClick={openUserModal}
          className="text-black text-2xl p-0 max-[369px]:text-lg"
        >
          <FaBell />
        </Button>
        <button
          type="button"
          onClick={open ? onClose : showDrawer}
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          className="flex items-center justify-center h-10 w-10 rounded-full bg-gray-100 hover:bg-gray-200 active:scale-95 transition-all duration-150 text-gray-700 text-lg max-[369px]:h-9 max-[369px]:w-9 max-[369px]:text-base"
        >
          <span
            className={`transition-transform duration-200 ${open ? "rotate-90" : "rotate-0"}`}
          >
            {open ? <CloseOutlined /> : <MenuOutlined />}
          </span>
        </button>
      </div>

      {/* Drawer for Mobile Menu */}
      <Drawer
        title={null}
        closeIcon={null}
        width={300}
        onClose={onClose}
        open={open}
        styles={{ body: { padding: 0 } }}
      >
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-4 border-b border-gray-100">
            <img src={mcareLogo} alt="Medpharma" className="h-8" />
            <button
              type="button"
              onClick={onClose}
              aria-label="Close menu"
              className="text-gray-400 hover:text-gray-600 text-lg leading-none p-1"
            >
              <CloseOutlined />
            </button>
          </div>

          {/* User summary */}
          <button
            type="button"
            onClick={() => {
              onClose();
              navigate("/main/profile/1");
            }}
            className="flex items-center gap-3 px-4 py-4 border-b border-gray-100 hover:bg-gray-50 transition-colors text-left w-full"
          >
            <div className="h-11 w-11 rounded-full overflow-hidden border border-gray-200 shrink-0">
              <img
                src={userImage}
                alt=""
                className="h-full w-full object-cover"
              />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">
                {user?.fullname}
              </p>
              <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            </div>
          </button>

          {/* Nav */}
          <nav className="flex-1 overflow-y-auto px-3 py-3 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                onClick={onClose}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl text-[15px] font-medium transition-colors ${
                    isActive
                      ? "bg-[#E6F2FD] text-[#06509E]"
                      : "text-gray-700 hover:bg-gray-50"
                  }`
                }
              >
                <span className="text-lg shrink-0">{item.icon}</span>
                {item.label}
              </NavLink>
            ))}

            <div className="my-2 border-t border-gray-100" />

            <button
              type="button"
              onClick={() => {
                onClose();
                onLogout();
              }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-[15px] font-medium text-red-600 hover:bg-red-50 transition-colors"
            >
              <LogoutOutlined className="text-lg shrink-0" />
              Logout
            </button>
          </nav>

          {/* Help footer */}
          <div className="px-4 py-4 border-t border-gray-100 bg-gray-50">
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
              <PhoneOutlined />
              Need help? Call us
            </div>
            <a
              href="tel:+233303000448"
              className="text-[#06509E] font-semibold text-base hover:underline"
            >
              030 300 0448
            </a>
          </div>
        </div>
      </Drawer>
    </div>
  );
}
